import { useContext, useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ComplianceRequestContext, ConfigContext } from '@/context';
import { api } from '@/utils/api';
import { handleError } from '@/utils/error';
import { useError } from '@/components/ui/useError';
import { Alert } from '@/components/ui/Alert';
import { WarningIcon, CheckCircleIcon } from '@phosphor-icons/react';

type Props = {
  onCancel: () => void;
  onSubmit: () => void;
};

export function ComplianceRequestVerify({ onCancel, onSubmit }: Props) {
  const { complianceRequest, setComplianceRequest } = useContext(
    ComplianceRequestContext,
  );
  const {
    errorTitle,
    getComplianceRequestTitle,
    privacyRequestsVerificationEmailDone,
    privacyRequestsVerificationEmailTitle,
    privacyRequestsVerificationEmailText,
    privacyRequestsVerificationEmailNoEmailText,
    privacyRequestsVerificationEmailProcessingError,
  } = useContext(ConfigContext);

  const { hasError, showError, hideError } = useError();
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    // Prevent multiple verification attempts
    if (verificationAttempted || isVerifying) return;

    // Check for magic link verification in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const verifyToken = urlParams.get('cord_verify');
    const requestId = urlParams.get('cord_request');

    // If we have URL parameters for verification, handle it immediately
    if (verifyToken && requestId) {
      setVerificationAttempted(true);
      setIsVerifying(true);
      verifyComplianceRequestByToken(verifyToken, requestId);
    }
  }, []);

  async function verifyComplianceRequestByToken(
    token: string,
    requestId?: string,
  ) {
    if (isVerifying) {
      return;
    }

    try {
      // Use provided requestId or fall back to context
      const idToUse = requestId || complianceRequest?.id;

      if (!idToUse) {
        setIsVerifying(false);
        return;
      }

      await api.compliance.verifyComplianceRequest(idToUse, token);

      hideError();
      setComplianceRequest(null);

      // Clean up URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete('cord_verify');
      url.searchParams.delete('cord_request');
      window.history.replaceState({}, '', url.toString());

      setIsVerifying(false);
      onSubmit();
    } catch (err) {
      // Handle specific error cases
      const errorMessage = err instanceof Error ? err.message : String(err);
      if (errorMessage.includes('not pending verification')) {
        hideError();
        setComplianceRequest(null);

        // Clean up URL parameters
        const url = new URL(window.location.href);
        url.searchParams.delete('cord_verify');
        url.searchParams.delete('cord_request');
        window.history.replaceState({}, '', url.toString());

        setIsVerifying(false);
        onSubmit();
        return;
      }

      setIsVerifying(false);
      handleError(err);
      showError();
    }
  }

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <Card title="Verifying...">
        <div className="space-y-5">
          <p>Verifying your compliance request...</p>
        </div>
      </Card>
    );
  }

  if (!complianceRequest || !complianceRequest?.id) {
    return null;
  }

  const title = getComplianceRequestTitle(complianceRequest);

  if (complianceRequest?.status === 'pending_verification') {
    return (
      <Card title={title}>
        <div className="space-y-5">
          <div className="flex items-center space-x-3">
            <CheckCircleIcon
              size={24}
              weight="fill"
              className="text-green-500"
            />
            <h2 className="text-subheader text-lg font-medium">
              {privacyRequestsVerificationEmailTitle}
            </h2>
          </div>

          <p className="text-muted-foreground">
            {privacyRequestsVerificationEmailText}{' '}
            {privacyRequestsVerificationEmailNoEmailText}
          </p>

          {hasError && (
            <Alert
              icon={<WarningIcon size={18} weight="fill" />}
              subTitle={privacyRequestsVerificationEmailProcessingError}
              title={errorTitle}
              variant="error"
            />
          )}

          <div className="flex gap-2">
            <div className="flex-grow" />
            <Button
              data-testid="cd-done-button"
              variant="pc-outline"
              onClick={onCancel}
            >
              {privacyRequestsVerificationEmailDone}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return null;
}
